/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {error} from '@ion/core/src/logger/logger';
import {
  createIOProfile,
  deleteIOProfile as destroyIOProfile,
  loadIOProfiles,
  updateIOProfile
} from '@ion/core/src/services/Connect';
import {
  Entity,
  IOProfile
} from '@ion/core/src/types/entities';
import {LoadIOProfilesParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageInitializeAction} from '@/types/redux';
import {
  errorAddOptions,
  errorEditOptions,
  successAddOptions,
  successEditOptions
} from '../../components/prompts/constants';
import {getAccountId} from '../helpers';
import {toggleUserPrompt} from './user-prompt';

// name of the reducer
export const NAME                               = 'adminIOProfiles';

// initialize ioprofiles ...
export const INITIALIZE_ADMIN_IOPROFILES        = 'INITIALIZE_ADMIN_IOPROFILES';
export const RE_INITIALIZE_ADMIN_IOPROFILES     = 'RE_INITIALIZE_ADMIN_IOPROFILES';

// refresh grid ...
export const ADMIN_IOPROFILES_GRID_REFRESHED    = 'ADMIN_IOPROFILES_GRID_REFRESHED';

// grid column state
export const SET_GRID_COLUMNS                   = 'SET_ADMIN_IOPROFILES_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS                = 'UPDATE_ADMIN_IOPROFILES_GRID_COLUMNS';

// ioprofile retrieval ...
export const GET_ADMIN_IOPROFILES_REQUEST       = 'GET_ADMIN_IOPROFILES_REQUEST';
export const GET_ADMIN_IOPROFILES_RESPONSE      = 'GET_ADMIN_IOPROFILES_RESPONSE';
export const GET_ADMIN_IOPROFILES_ERROR         = 'GET_ADMIN_IOPROFILES_ERROR';

// ioprofile add ...
export const ADD_ADMIN_IOPROFILE                = 'ADD_ADMIN_IOPROFILE';
export const ADD_ADMIN_IOPROFILE_REQUEST        = 'ADD_ADMIN_IOPROFILE_REQUEST';
export const ADD_ADMIN_IOPROFILE_RESPONSE       = 'ADD_ADMIN_IOPROFILE_RESPONSE';
export const ADD_ADMIN_IOPROFILE_ERROR          = 'ADD_ADMIN_IOPROFILE_ERROR';

// ioprofile edit ...
export const EDIT_ADMIN_IOPROFILE               = 'EDIT_ADMIN_IOPROFILE';
export const EDIT_ADMIN_IOPROFILE_REQUEST       = 'EDIT_ADMIN_IOPROFILE_REQUEST';
export const EDIT_ADMIN_IOPROFILE_RESPONSE      = 'EDIT_ADMIN_IOPROFILE_RESPONSE';
export const EDIT_ADMIN_IOPROFILE_ERROR         = 'EDIT_ADMIN_IOPROFILE_ERROR';

// view ioprofile audit log ...
export const AUDIT_ADMIN_IOPROFILE              = 'AUDIT_ADMIN_IOPROFILE';
export const AUDIT_ADMIN_IOPROFILE_REQUEST      = 'AUDIT_ADMIN_IOPROFILE_REQUEST';
export const AUDIT_ADMIN_IOPROFILE_RESPONSE     = 'AUDIT_ADMIN_IOPROFILE_RESPONSE';
export const AUDIT_ADMIN_IOPROFILE_ERROR        = 'AUDIT_ADMIN_IOPROFILE_ERROR';

// ioprofile delete ...
export const DELETE_ADMIN_IOPROFILE             = 'DELETE_ADMIN_IOPROFILE';
export const DELETE_ADMIN_IOPROFILE_REQUEST     = 'DELETE_ADMIN_IOPROFILE_REQUEST';
export const DELETE_ADMIN_IOPROFILE_RESPONSE    = 'DELETE_ADMIN_IOPROFILE_RESPONSE';
export const DELETE_ADMIN_IOPROFILE_ERROR       = 'DELETE_ADMIN_IOPROFILE_ERROR';

// bulk ioprofile edits ...
export const EDIT_ADMIN_IOPROFILES              = 'EDIT_ADMIN_IOPROFILES';
export const EDIT_ADMIN_IOPROFILES_REQUEST      = 'EDIT_ADMIN_IOPROFILES_REQUEST';
export const EDIT_ADMIN_IOPROFILES_RESPONSE     = 'EDIT_ADMIN_IOPROFILES_RESPONSE';
export const EDIT_ADMIN_IOPROFILES_ERROR        = 'EDIT_ADMIN_IOPROFILES_ERROR';

// bulk ioprofile deletes ...
export const DELETE_ADMIN_IOPROFILES            = 'DELETE_ADMIN_IOPROFILES';
export const DELETE_ADMIN_IOPROFILES_REQUEST    = 'DELETE_ADMIN_IOPROFILES_REQUEST';
export const DELETE_ADMIN_IOPROFILES_RESPONSE   = 'DELETE_ADMIN_IOPROFILES_RESPONSE';
export const DELETE_ADMIN_IOPROFILES_ERROR      = 'DELETE_ADMIN_IOPROFILES_ERROR';

// admin schedules filter model ...
export const SET_ADMIN_IOPROFILES_GRID_FILTER_MODEL = 'SET_ADMIN_IOPROFILES_GRID_FILTER_MODEL';

/**
 * @initializeAdminIOProfiles
 */
export const initializeAdminIOProfiles = (): PageInitializeAction => {
  return {type: INITIALIZE_ADMIN_IOPROFILES};
};

export const reInitializeAdminIOProfiles = (): PageInitializeAction => {
  return {type: RE_INITIALIZE_ADMIN_IOPROFILES};
};

/**
 * @adminIOProfilesGridRefreshed
 */
export const adminIOProfilesGridRefreshed = (): PageInitializeAction => {
  return {type: ADMIN_IOPROFILES_GRID_REFRESHED};
};

/**
 * @getAdminIOProfiles
 */
export const getAdminIOProfiles = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter,
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_ADMIN_IOPROFILES_REQUEST});
    const data: LoadIOProfilesParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const {ioprofiles: items, totalCount} = await loadIOProfiles(data);
      dispatch({type: GET_ADMIN_IOPROFILES_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      callbackFn({error: err, items: [], totalCount: 0});
      dispatch({type: GET_ADMIN_IOPROFILES_ERROR, error: err});
      return err;
    }
  };
};

/**
 * @addAdminIOProfile
 *
 * Invoked when the Add Admin IOProfile editor is to be displayed or removed.
 */
export const addAdminIOProfile = (show = true) => ({
  type : ADD_ADMIN_IOPROFILE, show
});

/**
 * @addIOProfile
 *
 * Invoked when a new IOProfile is to be created, delegating to the createIOProfile
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const addIOProfile = (ioprofile: IOProfile) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: ADD_ADMIN_IOPROFILE_REQUEST});
    try {
      const created = await createIOProfile(ioprofile);
      dispatch({type: ADD_ADMIN_IOPROFILE_RESPONSE, ioprofile: created});
      dispatch(toggleUserPrompt(successAddOptions(Entity.ioprofile)));
    } catch (err) {
      dispatch({type: ADD_ADMIN_IOPROFILE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorAddOptions(Entity.ioprofile)));
      error(err);
    }
  };
};

/**
 * @editAdminIOProfile
 *
 * Invoked when the Edit Admin IOProfile editor is to be displayed or removed.
 */
export const editAdminIOProfile = (ioprofile: IOProfile, show = true) => ({
  type : EDIT_ADMIN_IOPROFILE, ioprofile, show
});

/**
 * @editIOProfile
 *
 * Invoked when an existing IOProfile is to be updated, delegating to the updateIOProfile
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const editIOProfile = (ioprofile: IOProfile) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_IOPROFILE_REQUEST});
    try {
      const updated = await updateIOProfile(ioprofile);
      dispatch({type: EDIT_ADMIN_IOPROFILE_RESPONSE, ioprofile: updated});
      dispatch(toggleUserPrompt(successEditOptions(Entity.ioprofile)));
    } catch (err) {
      dispatch({type: EDIT_ADMIN_IOPROFILE_ERROR, error: err});
      dispatch(toggleUserPrompt(errorEditOptions(Entity.ioprofile)));
      error(err);
    }
  };
};

/**
 * @auditAdminIOProfile
 *
 * Invoked when the the ioprofile auditor is to be displayed or removed.
 */
export const auditAdminIOProfile = (ioprofile: IOProfile, show = true) => ({
  type : AUDIT_ADMIN_IOPROFILE, ioprofile, show
});

/**
 * @auditIOProfile
 *
 * Invoked when an ioprofile audit is to be made, delegating to the ioprofileAudit CTC
 * API and dispatching a successful response or error action accordingly.
 */
// [ION-APP TODO]: Implement Audit API once information is available
export const auditIOProfile = (ioprofile: IOProfile) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: AUDIT_ADMIN_IOPROFILE_REQUEST});
    try {
      // [ION_SUITE TODO]: const audited = await ioprofileAudit(ioprofile);
      // dispatch({type: AUDIT_ADMIN_IOPROFILE_RESPONSE, result: audited});
    } catch (err) {
      dispatch({type: AUDIT_ADMIN_IOPROFILE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @deleteAdminIOProfile
 *
 * Invoked when the Delete Admin IOProfile Dialog is to be displayed or removed.
 */
export const deleteAdminIOProfile = (ioprofile: IOProfile, show = true) => ({
  type : DELETE_ADMIN_IOPROFILE, ioprofile, show
});

/**
 * @deleteIOProfile
 *
 * Invoked when an existing ioprofile is to be deleted, delegating to the destroyIOProfile
 * CTC API and dispatching a successful response or error action accordingly.
 */
export const deleteIOProfile = (ioprofile: IOProfile) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_IOPROFILE_REQUEST});
    try {
      const deleted = await destroyIOProfile(ioprofile);
      dispatch({type: DELETE_ADMIN_IOPROFILE_RESPONSE, ioprofile: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_IOPROFILE_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @editAdminIOProfiles
 *
 * Invoked when the Bulk Edit Admin IOProfiles editor is to be displayed or removed.
 */
export const editAdminIOProfiles = (ioprofiles: IOProfile[], show = true) => ({
  type : EDIT_ADMIN_IOPROFILES, ioprofiles, show
});

/**
 * @editIOProfiles
 *
 * Invoked when a collection of existing ioprofiles are to be updated, delegating to
 * the updateIOProfiles CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const editIOProfiles = (ioprofiles: IOProfile[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: EDIT_ADMIN_IOPROFILES_REQUEST});
    try {
      // [ION_SUITE TODO]: const updated = await updateIOProfiles(ioprofiles);
      // dispatch({type: EDIT_ADMIN_IOPROFILES_RESPONSE, ioprofiles: updated});
    } catch (err) {
      dispatch({type: EDIT_ADMIN_IOPROFILES_ERROR, error: err});
      error(err);
    }
  };
};

/**
 * @deleteAdminIOProfiles
 *
 * Invoked when the Bulk Delete Admin IOProfiles Dialog is to be displayed or removed.
 */
export const deleteAdminIOProfiles = (ioprofiles: IOProfile[], show = true) => ({
  type : DELETE_ADMIN_IOPROFILES, ioprofiles, show
});

/**
 * @deleteIOProfiles
 *
 * Invoked when a collection of existing ioprofiles are to be deleted, delegating to
 * the destroyIOProfiles CTC API and dispatching a successful response or error action
 * accordingly.
 */
export const deleteIOProfiles = (ioprofiles: IOProfile[]) => {
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: DELETE_ADMIN_IOPROFILES_REQUEST});
    try {
      // [ION_SUITE TODO]: const deleted = await destroyIOProfiles(ioprofiles);
      // dispatch({type: DELETE_ADMIN_IOPROFILES_RESPONSE, ioprofiles: deleted});
    } catch (err) {
      dispatch({type: DELETE_ADMIN_IOPROFILES_ERROR, error: err});
      error(err);
    }
  };
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_ADMIN_IOPROFILES_GRID_FILTER_MODEL, model
});
